import React, { useEffect, useLayoutEffect, useState } from "react";
import ReactDOM from "react-dom";
import { BrowserRouter, useLocation } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import "antd/dist/antd.css";

import Router from "./router";
import i18n from "./translation";
import { getLanguageFromPath, SupportedLanguage } from "./languages";

const getInitialLanguage = (): SupportedLanguage => {
  return getLanguageFromPath(window.location.pathname);
};

const initialLanguage = getInitialLanguage();

i18n.changeLanguage(initialLanguage);
document.documentElement.lang = initialLanguage;

if ("scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

const LanguageHandler = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  const [language, setLanguage] = useState<SupportedLanguage>(() =>
    getLanguageFromPath(location.pathname)
  );

  useEffect(() => {
    const nextLanguage = getLanguageFromPath(location.pathname);

    document.documentElement.lang = nextLanguage;

    if (i18n.language !== nextLanguage) {
      i18n.changeLanguage(nextLanguage).then(() => {
        setLanguage(nextLanguage);
      });
    } else {
      setLanguage(nextLanguage);
    }
  }, [location.pathname]);

  return <React.Fragment key={language}>{children}</React.Fragment>;
};

const ScrollToRouteTarget = () => {
  const { pathname, search, hash } = useLocation();

  useLayoutEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
  }, [pathname, search, hash]);

  useEffect(() => {
    if (!hash) {
      return;
    }

    const element = document.getElementById(hash.substring(1));

    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  }, [pathname, search, hash]);

  return null;
};

const App = () => (
  <BrowserRouter>
    <I18nextProvider i18n={i18n}>
      <LanguageHandler>
        <ScrollToRouteTarget />
        <Router />
      </LanguageHandler>
    </I18nextProvider>
  </BrowserRouter>
);

ReactDOM.render(<App />, document.getElementById("root"));
